import { CLASSE_TABELA_ASSINATURA } from './quillTabelaAssinatura'

/**
 * Layouts antigos foram colados como HTML livre e podem usar tags que o Quill não
 * reconhece (ex.: <address>, <div>, <table>) — o Quill descarta silenciosamente
 * qualquer elemento fora do seu próprio conjunto de formatos ao adotar o HTML
 * inicial, apagando o conteúdo em vez de só perder a formatação. Aqui a tag em si
 * é removida (unwrap), mantendo o conteúdo/filhos intactos.
 *
 * Exceção: a tabela do bloco de assinatura (ql-tabela-assinatura) tem suporte de verdade
 * no editor (ver quillTabelaAssinatura.ts) — não deve ser desmontada.
 */
const TAGS_NAO_SUPORTADAS = 'address, div, section, article, header, footer, aside, table, thead, tbody, tr, td, th'

/**
 * Mesma detecção usada pelo sanitizador, exposta à parte para quem só precisa saber
 * "esse HTML é antigo/não suportado?" (ex.: aviso de revisão antes de salvar) sem
 * repetir a lista de tags em outro arquivo.
 */
export function possuiEstruturaNaoSuportada(html: string): boolean {
  if (!html) {
    return false
  }

  const doc = new DOMParser().parseFromString(html, 'text/html')

  return Array.from(doc.body.querySelectorAll(TAGS_NAO_SUPORTADAS))
    .some(elemento => !elemento.closest(`.${CLASSE_TABELA_ASSINATURA}`))
}

const BOM = String.fromCharCode(0xFEFF)
const CONTAINERS_DE_BLOCOS = 'body, ul, ol'
const TAGS_DE_BLOCO = ['P', 'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE', 'TABLE']

// Espaço entre dois trechos inline (<strong>a</strong> <em>b</em>) é conteúdo; só o espaço
// encostado em bloco (ou na borda do container) é artefato.
function separaBlocos(no: ChildNode): boolean {
  const ehBlocoOuBorda = (vizinho: ChildNode | null) => !vizinho || TAGS_DE_BLOCO.includes(vizinho.nodeName)

  return ehBlocoOuBorda(no.previousSibling) || ehBlocoOuBorda(no.nextSibling)
}

// Quebra de linha/espaço entre blocos vira um bloco vazio ao entrar no Quill (aparece como linha em
// branco no editor e some no e-mail). O cursor vazado do Quill (ql-cursor + U+FEFF) também não
// pode ser persistido.
export function limparArtefatosDoQuill(doc: Document) {
  doc.body.querySelectorAll('.ql-cursor').forEach(cursor => cursor.remove())

  const percorredor = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
  const textos: Text[] = []

  while (percorredor.nextNode()) {
    textos.push(percorredor.currentNode as Text)
  }

  textos.forEach((texto) => {
    if (texto.data.includes(BOM)) {
      texto.data = texto.data.split(BOM).join('')
    }
  })

  doc.querySelectorAll(CONTAINERS_DE_BLOCOS).forEach((container) => {
    Array.from(container.childNodes).forEach((no) => {
      if (no.nodeType === Node.TEXT_NODE && !(no.textContent || '').trim() && separaBlocos(no)) {
        no.remove()
      }
    })
  })

  doc.body.querySelectorAll('p').forEach((paragrafo) => {
    if (!paragrafo.children.length && !(paragrafo.textContent || '').trim()) {
      paragrafo.innerHTML = '<br>'
    }
  })
}

export default function sanitizarHtmlParaEditor(html: string): string {
  if (!html) {
    return html
  }

  const doc = new DOMParser().parseFromString(html, 'text/html')

  const candidatos = Array.from(doc.body.querySelectorAll(TAGS_NAO_SUPORTADAS))
    .filter(elemento => !elemento.closest(`.${CLASSE_TABELA_ASSINATURA}`))

  candidatos.forEach((elemento) => {
    const pai = elemento.parentNode

    if (!pai) {
      return
    }

    while (elemento.firstChild) {
      pai.insertBefore(elemento.firstChild, elemento)
    }

    elemento.remove()
  })

  limparArtefatosDoQuill(doc)

  return doc.body.innerHTML
}
